// Case.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import GridLayout from "react-grid-layout";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDrop } from "react-dnd";
import { useParams } from "react-router-dom";
import { Spin, Result } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import {
  ExamSidebar,
  EXAM_BAR_EXPANDED_HEIGHT,
  EXAM_BAR_COLLAPSED_HEIGHT,
} from "./ExamSidebar";
import HistologyViewer from "./HistologyViewer";
import RadiologyViewerWrapper from "./RadiologyViewerWrapper";
import MacroViewer from "./MacroViewer";
import GridItemWrapper from "./GridItemWrapper";
import ErrorBoundary from "./ErrorBoundary";
import CaseHeader, { CASE_HEADER_HEIGHT } from "./CaseHeader";
import AnnotationsDrawer from "./AnnotationsDrawer";
import ClinicalDrawer from "./ClinicalDrawer";
import PendingAnnotationModal from "./PendingAnnotationModal";
import { useAppContext } from "../context/AppContext";
import useAnnotations from "../hooks/useAnnotations";
import { calculateLayout, getNumCols } from "../utils/layout";
import { getAnnotationKey } from "../utils/annotationKey";
import { getCase } from "../api/cases";
import { listAnnotations } from "../api/annotations";
import { groupAnnotations } from "../api/adapters";

const HEADER_HEIGHT = 64;

// Inline fallback shown when a single viewer crashes, so it doesn't blank the page.
const ViewerCrash = (error) => (
  <div
    style={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: 16,
      textAlign: "center",
      color: "var(--color-text)",
      background: "var(--color-bg)",
    }}
  >
    <WarningOutlined style={{ fontSize: 28, color: "#faad14" }} />
    <div style={{ fontWeight: 600 }}>Impossible d'afficher cette vue</div>
    <div style={{ fontSize: 12, opacity: 0.7, maxWidth: 360, wordBreak: "break-word" }}>
      {error?.message || String(error)}
    </div>
  </div>
);

// Turn a case's API images[] into the grid's exam items. `content` carries
// everything a viewer + annotation needs: url, name, the stable assetId, thumbnail.
const buildExamsFromImages = (images = []) =>
  images.map((img) => ({
    id: `${img.modality}-${img.id}`,
    type: img.modality,
    content: {
      url: img.url,
      name: img.name,
      assetId: img.id,
      modality: img.modality,
      thumbnailUrl: img.thumbnailUrl,
    },
  }));

const GridArea = ({
  examBarHeight,
  mainSidebarCollapsed,
  getAvailableWidth,
  gridItems,
  layout,
  setLayout,
  onRemoveItem,
  onAddItem,
  renderableAnnotationsByKey,
  targetAnnotationIds,
  handleNewAnnotationData,
}) => {
  const { userRole } = useAppContext();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const updateDimensions = useCallback(() => {
    setDimensions({
      width: getAvailableWidth() - 24,
      height:
        window.innerHeight -
        HEADER_HEIGHT -
        CASE_HEADER_HEIGHT -
        examBarHeight -
        24,
    });
  }, [examBarHeight, mainSidebarCollapsed, getAvailableWidth]);

  useEffect(() => {
    updateDimensions();
    const handleResize = () => updateDimensions();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateDimensions]);

  const [, drop] = useDrop({
    accept: "EXAM",
    drop: (item) => onAddItem(item),
  });

  const renderViewer = (item) => {
    const { id: itemId, type, content } = item;
    const annotationKey = getAnnotationKey(item);
    const allForKey = renderableAnnotationsByKey[annotationKey] || [];
    // Multiple slides of the same modality share one annotationKey (e.g. two
    // histo slides both key as "histo"), so bind each viewer to its own asset:
    // render only annotations whose assetId matches this item (legacy ones with
    // no assetId fall through to all viewers of that key).
    const itemAnnotations = content.assetId
      ? allForKey.filter(
          (a) => a.assetId == null || a.assetId === content.assetId
        )
      : allForKey;
    const targetAnnotationId = targetAnnotationIds[annotationKey] || null;

    const onCompleted = (_passedId, data) =>
      handleNewAnnotationData(annotationKey, data, content.assetId);

    switch (type) {
      case "histo":
        return (
          <HistologyViewer
            itemId={itemId}
            dziUrl={content.url}
            annotations={itemAnnotations}
            targetAnnotationId={targetAnnotationId}
            showAnnotations={true}
            onAnnotationCompleted={onCompleted}
          />
        );
      case "radio":
        return (
          <RadiologyViewerWrapper
            itemId={itemId}
            seriesName={content.name}
            zipUrl={content.url}
            annotations={itemAnnotations}
            targetAnnotationId={targetAnnotationId}
            showAnnotations={true}
            onAnnotationCompleted={onCompleted}
            userRole={userRole}
          />
        );
      case "macro":
        return (
          <MacroViewer
            itemId={itemId}
            images={[content.url]}
            annotations={itemAnnotations}
            targetAnnotationId={targetAnnotationId}
            showAnnotations={true}
            onAnnotationCompleted={onCompleted}
          />
        );
      default:
        return null;
    }
  };

  const numCols = getNumCols(gridItems.length);
  const rowHeight = Math.max(
    100,
    dimensions.height / Math.ceil(Math.max(gridItems.length, 1) / numCols)
  );

  return (
    <div
      ref={drop}
      className="grid-area"
      style={{
        flex: 1,
        height: "100%",
        overflow: "hidden",
        position: "relative",
        background: "var(--color-bg)",
        color: "var(--color-text)",
      }}
    >
      <GridLayout
        className="layout"
        layout={layout}
        cols={12}
        rowHeight={rowHeight - 10}
        width={dimensions.width}
        containerPadding={[5, 5]}
        onLayoutChange={setLayout}
        isDraggable={false}
        isResizable={false}
        preventCollision={true}
        compactType={null}
      >
        {gridItems.map((item) => (
          <div
            key={item.id}
            className="grid-item"
            style={{
              position: "relative",
              backgroundColor: "var(--color-bg)",
              border: "1px solid var(--color-border)",
              height: "100%",
              overflow: "hidden",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            }}
          >
            <GridItemWrapper item={item} onRemove={onRemoveItem}>
              <ErrorBoundary fallback={ViewerCrash}>
                {renderViewer(item)}
              </ErrorBoundary>
            </GridItemWrapper>
          </div>
        ))}
      </GridLayout>
    </div>
  );
};

// Inner view — only mounted once case data + annotations are loaded, so the
// useAnnotations hook initializes with the real data.
const CaseContent = ({
  mainSidebarCollapsed,
  caseData,
  exams,
  initialAnnotations,
  initialKeys,
}) => {
  const [examSidebarCollapsed, setExamSidebarCollapsed] = useState(true);
  const { userRole } = useAppContext();

  const [gridItems, setGridItems] = useState(exams);
  const [layout, setLayout] = useState(() =>
    calculateLayout(exams, getNumCols(exams.length))
  );
  const [pendingGoTo, setPendingGoTo] = useState(null);

  // Map annotationKey -> exam item, to re-add a viewer for "Go to" if removed.
  const examsByKey = useRef(
    exams.reduce((acc, item) => {
      const k = getAnnotationKey(item);
      if (k) acc[k] = item;
      return acc;
    }, {})
  );

  const annotationHook = useAnnotations(
    initialAnnotations,
    initialKeys,
    caseData.key
  );
  const {
    visibleAnnotationsByKey,
    renderableAnnotationsByKey,
    sectionVisibility,
    annotationVisibilityById,
    authorsByRole,
    authorVisibility,
    handleSetAuthorVisibility,
    targetAnnotationIds,
    handleSetSectionVisibility,
    handleSetAnnotationVisibility,
    handleSetAnnotationColor,
    handleShowAllGlobal,
    handleHideAllGlobal,
    handleDeleteAnnotation,
    handleNewAnnotationData,
    handleGoToAnnotation,
    initVisibilityForKey,
    pendingAnnotationData,
    pendingModalOpen,
    newAnnotationName,
    setNewAnnotationName,
    newAnnotationDesc,
    setNewAnnotationDesc,
    newAnnotationColor,
    setNewAnnotationColor,
    handleSaveAnnotation,
    handleCancelPending,
    drawerOpen,
    setDrawerOpen,
  } = annotationHook;

  const totalAnnotations = useMemo(
    () =>
      Object.values(visibleAnnotationsByKey).reduce(
        (sum, list) => sum + (list?.length || 0),
        0
      ),
    [visibleAnnotationsByKey]
  );

  const [clinicalOpen, setClinicalOpen] = useState(false);

  const handleExamSidebarToggle = () =>
    setExamSidebarCollapsed(!examSidebarCollapsed);

  const getAvailableWidth = () => {
    const mainSidebarWidth = mainSidebarCollapsed ? 80 : 200;
    return window.innerWidth - mainSidebarWidth;
  };

  const examBarHeight = examSidebarCollapsed
    ? EXAM_BAR_COLLAPSED_HEIGHT
    : EXAM_BAR_EXPANDED_HEIGHT;

  const handleAddItem = useCallback(
    (item) => {
      const newId = `${item.type}-${Date.now()}`;
      const newItem = { id: newId, type: item.type, content: item.content };
      setGridItems((prev) => {
        const next = [...prev, newItem];
        setLayout(calculateLayout(next, getNumCols(next.length)));
        return next;
      });
      const key = getAnnotationKey(newItem);
      if (key) initVisibilityForKey(key);
      return newItem;
    },
    [initVisibilityForKey]
  );

  const handleRemoveItem = useCallback((id) => {
    setGridItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      setLayout(calculateLayout(next, getNumCols(next.length)));
      return next;
    });
  }, []);

  const handleDrawerGoTo = useCallback(
    (annotationKey, annotationId) => {
      const exists = gridItems.some(
        (i) => getAnnotationKey(i) === annotationKey
      );
      if (exists) {
        handleGoToAnnotation(annotationKey, annotationId);
        return;
      }
      const def = examsByKey.current[annotationKey];
      if (!def) return;
      handleAddItem(def);
      setPendingGoTo({ annotationKey, annotationId });
    },
    [gridItems, handleAddItem, handleGoToAnnotation]
  );

  // Trigger Go-to once the auto-added viewer is mounted (DICOM/OSD warmup).
  useEffect(() => {
    if (!pendingGoTo) return;
    const exists = gridItems.some(
      (i) => getAnnotationKey(i) === pendingGoTo.annotationKey
    );
    if (!exists) return;
    const t = setTimeout(() => {
      handleGoToAnnotation(pendingGoTo.annotationKey, pendingGoTo.annotationId);
      setPendingGoTo(null);
    }, 1200);
    return () => clearTimeout(t);
  }, [pendingGoTo, gridItems, handleGoToAnnotation]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          background: "var(--color-bg)",
          color: "var(--color-text)",
        }}
      >
        <CaseHeader
          selectedCase={caseData}
          annotationCount={totalAnnotations}
          onOpenAnnotations={() => setDrawerOpen((o) => !o)}
          onOpenClinical={() => setClinicalOpen((o) => !o)}
        />
        <div
          style={{
            display: "flex",
            flex: 1,
            position: "relative",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <GridArea
            examBarHeight={examBarHeight}
            mainSidebarCollapsed={mainSidebarCollapsed}
            getAvailableWidth={getAvailableWidth}
            gridItems={gridItems}
            layout={layout}
            setLayout={setLayout}
            onRemoveItem={handleRemoveItem}
            onAddItem={handleAddItem}
            renderableAnnotationsByKey={renderableAnnotationsByKey}
            targetAnnotationIds={targetAnnotationIds}
            handleNewAnnotationData={handleNewAnnotationData}
          />
        </div>
        <ExamSidebar
          isCollapsed={examSidebarCollapsed}
          onToggle={handleExamSidebarToggle}
          images={caseData.images}
        />

        <AnnotationsDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          visibleAnnotationsByKey={visibleAnnotationsByKey}
          sectionVisibility={sectionVisibility}
          annotationVisibilityById={annotationVisibilityById}
          authorsByRole={authorsByRole}
          authorVisibility={authorVisibility}
          onToggleAuthor={handleSetAuthorVisibility}
          userRole={userRole}
          onGoTo={handleDrawerGoTo}
          onDelete={handleDeleteAnnotation}
          onToggleSection={handleSetSectionVisibility}
          onToggleAnnotation={handleSetAnnotationVisibility}
          onSetColor={handleSetAnnotationColor}
          onShowAll={handleShowAllGlobal}
          onHideAll={handleHideAllGlobal}
        />

        <ClinicalDrawer
          open={clinicalOpen}
          onClose={() => setClinicalOpen(false)}
          caseData={caseData}
        />

        <PendingAnnotationModal
          open={pendingModalOpen}
          pendingAnnotationData={pendingAnnotationData}
          newAnnotationName={newAnnotationName}
          setNewAnnotationName={setNewAnnotationName}
          newAnnotationDesc={newAnnotationDesc}
          setNewAnnotationDesc={setNewAnnotationDesc}
          newAnnotationColor={newAnnotationColor}
          setNewAnnotationColor={setNewAnnotationColor}
          onSave={handleSaveAnnotation}
          onCancel={handleCancelPending}
        />
      </div>
    </DndProvider>
  );
};

const centerBox = {
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const CasePage = ({ collapsed: mainSidebarCollapsed }) => {
  const { caseId } = useParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    if (!caseId) {
      setState({ status: "empty" });
      return;
    }
    let active = true;
    setState({ status: "loading" });
    (async () => {
      try {
        const [caseData, annoList] = await Promise.all([
          getCase(caseId),
          listAnnotations(caseId),
        ]);
        if (!active) return;
        const exams = buildExamsFromImages(caseData.images);
        const initialAnnotations = groupAnnotations(annoList);
        const initialKeys = exams.map(getAnnotationKey).filter(Boolean);
        setState({ status: "ready", caseData, exams, initialAnnotations, initialKeys });
      } catch (e) {
        if (active) setState({ status: "error", message: e.message });
      }
    })();
    return () => {
      active = false;
    };
  }, [caseId]);

  if (state.status === "loading") {
    return (
      <div style={centerBox}>
        <Spin size="large" />
      </div>
    );
  }

  if (state.status === "empty") {
    return (
      <Result
        status="info"
        title="Aucun cas sélectionné"
        subTitle="Choisissez un cas depuis la liste pour l'ouvrir."
      />
    );
  }

  if (state.status === "error") {
    return (
      <Result
        status="error"
        title="Impossible de charger le cas"
        subTitle={state.message}
      />
    );
  }

  return (
    <CaseContent
      key={state.caseData.key}
      mainSidebarCollapsed={mainSidebarCollapsed}
      caseData={state.caseData}
      exams={state.exams}
      initialAnnotations={state.initialAnnotations}
      initialKeys={state.initialKeys}
    />
  );
};

export default CasePage;
